import React from 'react';
import { Plus } from 'lucide-react';
import type {
    AsplanUnternehmen, Beteiligter, MesskonzeptZeile, Messziel, PruefEintrag, PruefStatus, Unterlage,
} from '../types';
import { BEREICH_LABEL, PRUEFLISTE } from '../kataloge/pruefliste';
import type { KatalogVorschlag } from '../ablage/katalogVorschlaege';
import { AuswahlFeld, HakenFeld, Leerzeile, NeuZeile, TextFeld, Untertitel, ZeilenKarte } from './bausteine';
import { KatalogErklaerungZuUnterlage, MitgeltendeVorschlaege } from './MitgeltendeVorschlaege';

/**
 * Die uebrigen Listen des A+S-Plans: Beteiligte, Unternehmen, Unterlagen,
 * Messkonzept und der Selbstcheck.
 *
 * Sie stehen zusammen in einer Datei, weil sie denselben Zuschnitt haben —
 * eine Liste, eine Zeile, ein Loeschknopf. Getrennte Dateien mit je vierzig
 * Zeilen brauchten mehr Aufmerksamkeit beim Suchen als beim Lesen.
 */

// ═══════════════════════════════════════════════════════════════════════════
// BETEILIGTE
// ═══════════════════════════════════════════════════════════════════════════

const FUNKTIONEN = [
    { wert: 'auftraggeber', label: 'Auftraggeber' },
    { wert: 'bauherrenvertretung', label: 'Bauherrenvertretung' },
    { wert: 'arbeitsschutzbehoerde', label: 'Arbeitsschutzbehörde' },
    { wert: 'koordinator_dguv', label: 'Koordinator DGUV R 101-004' },
    { wert: 'sigeko', label: 'SiGeKo (BaustellV)' },
    { wert: 'bauleitung', label: 'Bauleitung' },
    { wert: 'geologe', label: 'Beratende Geologen' },
    { wert: 'probenehmer', label: 'Probenehmer' },
    { wert: 'gutachter', label: 'Schadstoffgutachten' },
    { wert: 'messstelle', label: 'Baubegleitende Messstelle' },
    { wert: 'bg', label: 'Berufsgenossenschaft' },
    { wert: 'arbeitsmediziner', label: 'Arbeitsmediziner' },
];

export const BeteiligteEditor: React.FC<{
    beteiligte: Beteiligter[];
    unternehmen: AsplanUnternehmen[];
    gesperrt: boolean;
    anlegen: (funktion: string) => Promise<unknown>;
    aendern: (id: string, u: Partial<Beteiligter>) => void;
    loeschen: (id: string) => void;
}> = ({ beteiligte, unternehmen, gesperrt, anlegen, aendern, loeschen }) => (
    <>
        <NeuZeile
            platzhalter="Funktion, z. B. Probenehmer"
            knopf="Beteiligten"
            gesperrt={gesperrt}
            onAnlegen={anlegen}
        />

        {beteiligte.length === 0 ? (
            <Leerzeile text="Noch niemand erfasst. Anhang 3 Nr. 1 verlangt Auftraggeber, Behörde, Gutachter und Koordinator." />
        ) : (
            <ul className="space-y-2">
                {beteiligte.map(p => (
                    <ZeilenKarte
                        key={p.id}
                        gesperrt={gesperrt}
                        onLoeschen={() => loeschen(p.id)}
                        loeschTitel={`${p.funktion} löschen`}
                        // Die Herkunft muss sichtbar sein: nur diese Zeilen
                        // werden bei einer erneuten Uebernahme nachgezogen.
                        // Wer das nicht sieht, wundert sich ueber eine
                        // Nummer, die er selbst geaendert zu haben glaubt.
                        kopf={p.quelle === 'projekt' ? (
                            <span className="inline-block px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 text-[10px] font-bold uppercase tracking-wider">
                                aus dem Projekt übernommen
                            </span>
                        ) : undefined}
                    >
                        <div className="grid sm:grid-cols-3 gap-2 mb-2">
                            <TextFeld
                                label="Funktion"
                                wert={p.funktion}
                                gesperrt={gesperrt}
                                onSpeichern={v => aendern(p.id, { funktion: v })}
                            />
                            <AuswahlFeld
                                label="Art"
                                wert={p.funktion_key}
                                optionen={FUNKTIONEN}
                                gesperrt={gesperrt}
                                onSpeichern={v => aendern(p.id, { funktion_key: v })}
                            />
                            <TextFeld
                                label="Organisation"
                                wert={p.organisation}
                                gesperrt={gesperrt}
                                onSpeichern={v => aendern(p.id, { organisation: v })}
                            />
                        </div>
                        <div className="grid sm:grid-cols-4 gap-2">
                            <TextFeld
                                label="Person"
                                wert={p.person}
                                gesperrt={gesperrt}
                                onSpeichern={v => aendern(p.id, { person: v })}
                            />
                            <TextFeld
                                label="Anschrift"
                                wert={p.anschrift}
                                gesperrt={gesperrt}
                                onSpeichern={v => aendern(p.id, { anschrift: v })}
                            />
                            <TextFeld
                                label="Telefon / Mobil"
                                wert={p.mobil || p.telefon}
                                gesperrt={gesperrt}
                                onSpeichern={v => aendern(p.id, { mobil: v })}
                            />
                            <TextFeld
                                label="E-Mail"
                                wert={p.email}
                                gesperrt={gesperrt}
                                onSpeichern={v => aendern(p.id, { email: v })}
                            />
                        </div>

                        {p.funktion_key === 'bg' && (
                            <div className="mt-2">
                                <Untertitel
                                    text="Zuständig für"
                                    hinweis="Die Anzeige nach Abschnitt 11.2 geht an die BG des jeweiligen Unternehmens — auf einer Baustelle können mehrere zuständig sein."
                                />
                                <div className="flex flex-wrap gap-1.5">
                                    {unternehmen.length === 0 && (
                                        <span className="text-xs text-slate-400">Noch kein Unternehmen erfasst.</span>
                                    )}
                                    {unternehmen.map(u => {
                                        const drin = p.gilt_fuer.includes(u.unternehmen);
                                        return (
                                            <button
                                                key={u.id}
                                                type="button"
                                                disabled={gesperrt}
                                                onClick={() => aendern(p.id, {
                                                    gilt_fuer: drin
                                                        ? p.gilt_fuer.filter(x => x !== u.unternehmen)
                                                        : [...p.gilt_fuer, u.unternehmen],
                                                })}
                                                className={`px-2 py-0.5 rounded text-xs font-semibold border ${
                                                    drin ? 'bg-slate-800 text-white border-slate-800'
                                                        : 'border-slate-300 text-slate-600'
                                                }`}
                                            >
                                                {u.unternehmen}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </ZeilenKarte>
                ))}
            </ul>
        )}
    </>
);

// ═══════════════════════════════════════════════════════════════════════════
// UNTERNEHMEN
// ═══════════════════════════════════════════════════════════════════════════

export const UnternehmenEditor: React.FC<{
    unternehmen: AsplanUnternehmen[];
    gesperrt: boolean;
    anlegen: (name: string) => Promise<unknown>;
    aendern: (id: string, u: Partial<AsplanUnternehmen>) => void;
    loeschen: (id: string) => void;
}> = ({ unternehmen, gesperrt, anlegen, aendern, loeschen }) => (
    <>
        <NeuZeile
            platzhalter="Unternehmen"
            knopf="Unternehmen"
            gesperrt={gesperrt}
            onAnlegen={anlegen}
        />

        {unternehmen.length === 0 ? (
            <Leerzeile text="Noch kein Unternehmen erfasst." />
        ) : (
            <ul className="space-y-2">
                {unternehmen.map(u => (
                    <ZeilenKarte
                        key={u.id}
                        gesperrt={gesperrt}
                        onLoeschen={() => loeschen(u.id)}
                        loeschTitel={`${u.unternehmen} löschen`}
                    >
                        <div className="grid sm:grid-cols-4 gap-2 mb-2">
                            <TextFeld
                                label="Unternehmen"
                                wert={u.unternehmen}
                                gesperrt={gesperrt}
                                onSpeichern={v => aendern(u.id, { unternehmen: v })}
                            />
                            <TextFeld
                                label="Gewerk"
                                wert={u.gewerk}
                                gesperrt={gesperrt}
                                onSpeichern={v => aendern(u.id, { gewerk: v })}
                            />
                            <AuswahlFeld
                                label="Rolle"
                                wert={u.rolle_im_projekt}
                                optionen={[
                                    { wert: 'an' as const, label: 'Auftragnehmer' },
                                    { wert: 'nu' as const, label: 'Nachunternehmer' },
                                    { wert: 'arge' as const, label: 'ARGE' },
                                ]}
                                gesperrt={gesperrt}
                                onSpeichern={v => aendern(u.id, { rolle_im_projekt: v })}
                            />
                            <TextFeld
                                label="Aufsichtsführender"
                                wert={u.aufsichtsfuehrender}
                                gesperrt={gesperrt}
                                onSpeichern={v => aendern(u.id, { aufsichtsfuehrender: v })}
                            />
                        </div>

                        <div className="flex flex-wrap items-center gap-4">
                            <HakenFeld
                                label="Sachkunde nach Anhang 6A vorgelegt"
                                wert={u.sachkunde_vorgelegt}
                                gesperrt={gesperrt}
                                onSpeichern={v => aendern(u.id, { sachkunde_vorgelegt: v })}
                            />
                            <span className="w-56">
                                <AuswahlFeld
                                    label="Verpflichtungserklärung"
                                    wert={u.erklaerung_status}
                                    optionen={[
                                        { wert: 'offen' as const, label: 'offen' },
                                        { wert: 'ausgegeben' as const, label: 'ausgegeben' },
                                        { wert: 'zurueck' as const, label: 'liegt unterschrieben vor' },
                                    ]}
                                    gesperrt={gesperrt}
                                    onSpeichern={v => aendern(u.id, { erklaerung_status: v })}
                                />
                            </span>
                            <span className="w-56">
                                <AuswahlFeld
                                    label="Arbeitsmediziner"
                                    wert={u.arbeitsmediziner_status}
                                    optionen={[
                                        { wert: 'offen' as const, label: 'noch nicht benannt' },
                                        { wert: 'benannt' as const, label: 'benannt' },
                                    ]}
                                    gesperrt={gesperrt}
                                    onSpeichern={v => aendern(u.id, { arbeitsmediziner_status: v })}
                                />
                            </span>
                        </div>
                    </ZeilenKarte>
                ))}
            </ul>
        )}
    </>
);

// ═══════════════════════════════════════════════════════════════════════════
// UNTERLAGEN UND ANLAGEN
// ═══════════════════════════════════════════════════════════════════════════

export const UnterlagenEditor: React.FC<{
    unterlagen: Unterlage[];
    gesperrt: boolean;
    anlegen: (bezeichnung: string) => Promise<unknown>;
    /** Vorschläge aus dem Katalog mitgeltender Dokumente übernehmen. */
    ausKatalog?: (vorschlaege: readonly KatalogVorschlag[]) => Promise<number | null>;
    aendern: (id: string, u: Partial<Unterlage>) => void;
    loeschen: (id: string) => void;
}> = ({ unterlagen, gesperrt, anlegen, ausKatalog, aendern, loeschen }) => (
    <>
        <NeuZeile
            platzhalter="Bezeichnung der Unterlage"
            knopf="Unterlage"
            gesperrt={gesperrt}
            breite="w-72"
            onAnlegen={anlegen}
        />

        {unterlagen.length === 0 ? (
            <Leerzeile text="Noch keine Unterlage benannt." />
        ) : (
            <ul className="space-y-2">
                {unterlagen.map(u => (
                    <ZeilenKarte
                        key={u.id}
                        gesperrt={gesperrt}
                        onLoeschen={() => loeschen(u.id)}
                        loeschTitel="Unterlage löschen"
                    >
                        <div className="grid sm:grid-cols-5 gap-2">
                            <AuswahlFeld
                                label="Art"
                                wert={u.art}
                                optionen={[
                                    { wert: 'grundlage' as const, label: 'verwendete Unterlage' },
                                    { wert: 'anlage' as const, label: 'Anlage des Plans' },
                                ]}
                                gesperrt={gesperrt}
                                onSpeichern={v => aendern(u.id, { art: v })}
                            />
                            <TextFeld
                                label="Herausgeber"
                                wert={u.herausgeber}
                                gesperrt={gesperrt}
                                onSpeichern={v => aendern(u.id, { herausgeber: v })}
                            />
                            <span className="sm:col-span-2">
                                <TextFeld
                                    label="Bezeichnung"
                                    wert={u.bezeichnung}
                                    breit
                                    gesperrt={gesperrt}
                                    onSpeichern={v => aendern(u.id, { bezeichnung: v })}
                                />
                            </span>
                            <TextFeld
                                label="Datum (JJJJ-MM-TT)"
                                wert={u.datum ?? ''}
                                gesperrt={gesperrt}
                                onSpeichern={v => aendern(u.id, { datum: v || null })}
                            />
                        </div>
                        <div className="mt-1.5">
                            <HakenFeld
                                label="wird nachgereicht"
                                wert={u.nachgereicht}
                                gesperrt={gesperrt}
                                onSpeichern={v => aendern(u.id, { nachgereicht: v })}
                            />
                        </div>
                        {/* Auch fuer Zeilen, die von Hand und nicht ueber
                            einen Vorschlag entstanden sind. */}
                        <KatalogErklaerungZuUnterlage bezeichnung={u.bezeichnung} />
                    </ZeilenKarte>
                ))}
            </ul>
        )}

        {ausKatalog && (
            <MitgeltendeVorschlaege
                unterlagen={unterlagen}
                gesperrt={gesperrt}
                uebernehmen={ausKatalog}
            />
        )}
    </>
);

// ═══════════════════════════════════════════════════════════════════════════
// MESSKONZEPT
// ═══════════════════════════════════════════════════════════════════════════

const MESSZIELE: { wert: Messziel; label: string }[] = [
    { wert: 'akut', label: 'Akutgefahr (kontinuierlich mit Warngerät)' },
    { wert: 'chronisch', label: 'chronisch wirkende Stoffe (CMR)' },
    { wert: 'wirksamkeit', label: 'Wirksamkeit der Schutzmaßnahmen' },
    { wert: 'grenzwert', label: 'Grenzwerte, Akzeptanz-/Toleranzkonzentration' },
];

const LEERE_ZEILE: MesskonzeptZeile = {
    messziel: 'wirksamkeit', parameter: '', massstab: '', methodik: '',
    haeufigkeit: '', geraet: '', kalibrierung: '', alarmschwelle: '',
    schwellenwert: '', massnahme: '', verantwortlich: '',
};

export const MesskonzeptEditor: React.FC<{
    zeilen: MesskonzeptZeile[];
    gesperrt: boolean;
    onSpeichern: (zeilen: MesskonzeptZeile[]) => void;
}> = ({ zeilen, gesperrt, onSpeichern }) => {
    const aendern = (i: number, u: Partial<MesskonzeptZeile>) =>
        onSpeichern(zeilen.map((z, j) => (j === i ? { ...z, ...u } : z)));

    return (
        <>
            {zeilen.length === 0 && (
                <Leerzeile text="Noch kein Messkonzept — Abschnitt 9 der DGUV Regel 101-004 verlangt eines." />
            )}

            <ul className="space-y-2">
                {zeilen.map((z, i) => (
                    <ZeilenKarte
                        key={i}
                        gesperrt={gesperrt}
                        onLoeschen={() => onSpeichern(zeilen.filter((_, j) => j !== i))}
                        loeschTitel="Zeile löschen"
                    >
                        <div className="grid sm:grid-cols-3 gap-2 mb-2">
                            <AuswahlFeld
                                label="Messziel (TRGS 524 Anlage 9)"
                                wert={z.messziel}
                                optionen={MESSZIELE}
                                gesperrt={gesperrt}
                                onSpeichern={v => aendern(i, { messziel: v })}
                            />
                            <TextFeld
                                label="Parameter"
                                wert={z.parameter}
                                platzhalter="A-Staub"
                                gesperrt={gesperrt}
                                onSpeichern={v => aendern(i, { parameter: v })}
                            />
                            <TextFeld
                                label="Bewertungsmaßstab"
                                wert={z.massstab}
                                platzhalter="TRGS 900"
                                gesperrt={gesperrt}
                                onSpeichern={v => aendern(i, { massstab: v })}
                            />
                        </div>
                        <div className="grid sm:grid-cols-3 gap-2 mb-2">
                            <TextFeld
                                label="Verfahren"
                                wert={z.methodik}
                                gesperrt={gesperrt}
                                onSpeichern={v => aendern(i, { methodik: v })}
                            />
                            <TextFeld
                                label="Gerät"
                                wert={z.geraet}
                                gesperrt={gesperrt}
                                onSpeichern={v => aendern(i, { geraet: v })}
                            />
                            <TextFeld
                                label="Kalibrierung"
                                wert={z.kalibrierung}
                                gesperrt={gesperrt}
                                onSpeichern={v => aendern(i, { kalibrierung: v })}
                            />
                        </div>
                        <div className="grid sm:grid-cols-4 gap-2">
                            <TextFeld
                                label="Häufigkeit"
                                wert={z.haeufigkeit}
                                gesperrt={gesperrt}
                                onSpeichern={v => aendern(i, { haeufigkeit: v })}
                            />
                            <TextFeld
                                label={z.messziel === 'akut' ? 'Alarmschwelle (Pflicht)' : 'Alarmschwelle'}
                                wert={z.alarmschwelle}
                                gesperrt={gesperrt}
                                onSpeichern={v => aendern(i, { alarmschwelle: v })}
                            />
                            <TextFeld
                                label="Schwellenwert"
                                wert={z.schwellenwert}
                                platzhalter="10 % AGW"
                                gesperrt={gesperrt}
                                onSpeichern={v => aendern(i, { schwellenwert: v })}
                            />
                            <TextFeld
                                label="Auszulösende Maßnahme"
                                wert={z.massnahme}
                                gesperrt={gesperrt}
                                onSpeichern={v => aendern(i, { massnahme: v })}
                            />
                        </div>
                    </ZeilenKarte>
                ))}
            </ul>

            {!gesperrt && (
                <button
                    type="button"
                    onClick={() => onSpeichern([...zeilen, LEERE_ZEILE])}
                    className="inline-flex items-center gap-1 mt-2 text-[11px] font-bold text-stone-700 underline"
                >
                    <Plus className="w-3 h-3" /> Messzeile
                </button>
            )}
        </>
    );
};

// ═══════════════════════════════════════════════════════════════════════════
// SELBSTCHECK
// ═══════════════════════════════════════════════════════════════════════════

const STATUS: { wert: PruefStatus; label: string }[] = [
    { wert: 'offen', label: 'offen' },
    { wert: 'erfuellt', label: 'erfüllt' },
    { wert: 'maengel', label: 'Mängel festgestellt' },
    { wert: 'nicht_zutreffend', label: 'trifft nicht zu' },
];

export const PrueflisteEditor: React.FC<{
    stand: PruefEintrag[];
    auto: { check_key: string; erfuellt: boolean; bemerkung: string }[];
    gesperrt: boolean;
    setzen: (check_key: string, status: PruefStatus, bemerkung: string) => void;
}> = ({ stand, auto, gesperrt, setzen }) => (
    <>
        {(Object.keys(BEREICH_LABEL) as (keyof typeof BEREICH_LABEL)[]).map(bereich => {
            const punkte = PRUEFLISTE.filter(p => p.bereich === bereich);
            if (punkte.length === 0) return null;
            return (
                <div key={bereich} className="mb-4 last:mb-0">
                    <Untertitel text={BEREICH_LABEL[bereich]} />
                    <ul className="space-y-2">
                        {punkte.map(p => {
                            const a = auto.find(x => x.check_key === p.key);
                            const eigen = stand.find(x => x.check_key === p.key);
                            return (
                                <li key={p.key} className="border border-slate-200 rounded-lg p-2.5">
                                    <p className="text-sm text-slate-800">{p.frage}</p>
                                    <p className="text-[11px] text-slate-500 mt-0.5">{p.warum}</p>

                                    <div className="flex flex-wrap items-center gap-2 mt-2">
                                        {a && (
                                            <span className={`px-2 py-0.5 rounded text-[11px] font-bold ${
                                                a.erfuellt ? 'bg-emerald-50 text-emerald-700'
                                                    : 'bg-amber-50 text-amber-800'
                                            }`}>
                                                automatisch: {a.erfuellt ? 'erfüllt' : 'offen'}
                                            </span>
                                        )}
                                        <span className="w-52">
                                            <AuswahlFeld
                                                wert={eigen?.status ?? 'offen'}
                                                optionen={STATUS}
                                                gesperrt={gesperrt}
                                                onSpeichern={v => setzen(p.key, v, eigen?.bemerkung ?? '')}
                                            />
                                        </span>
                                        <span className="flex-1 min-w-[14rem]">
                                            <TextFeld
                                                wert={eigen?.bemerkung ?? ''}
                                                platzhalter={
                                                    eigen?.status === 'nicht_zutreffend'
                                                        ? 'Begründung — „trifft nicht zu“ ohne Grund ist derselbe Mangel'
                                                        : 'Bemerkung'
                                                }
                                                gesperrt={gesperrt}
                                                onSpeichern={v => setzen(p.key, eigen?.status ?? 'offen', v)}
                                            />
                                        </span>
                                    </div>
                                    <p className="text-[10px] text-slate-400 mt-1">
                                        {p.nummer} · {p.kapitel}{!p.automatisch && ' · Urteilsfrage'}
                                    </p>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            );
        })}
    </>
);
